/**
 * Gets the id of a step's parent.
 */
const getParentStepId = (stepId, dag = {}) => {
  for (const id in dag) {
    if (dag[id].exit === stepId || dag[id].child === stepId || dag[id].branches?.includes(stepId)) {
      return id;
    }
  }
};

/**
 * Follows a folder's exit steps until it reaches the end.
 */
const getFolderExitStepId = (folderRootId, dag = {}) => {
  const iterate = (stepId) => {
    const step = dag[stepId];
    if (!step?.exit) {
      return stepId;
    }
    return iterate(step.exit);
  };
  return iterate(folderRootId);
};

/**
 * Follows a step's exit steps until it reaches the end and returns an array.
 */
// eslint-disable-next-line no-unused-vars
const getAllExitIds = (stepId, dag, array) => {
  const step = dag[stepId] || {};
  if (step?.exit) {
    array.push(step.exit);
    getAllExitIds(step.exit, dag, array);
  }
  if (step?.child) {
    array.push(step.child);
    getAllExitIds(step.child, dag, array);
  }
  if (step?.branches) {
    for (let i = 0; i < step.branches.length; i++) {
      array.push(step.branches[i]);
      getAllExitIds(step.branches[i], dag, array);
    }
  }
  return array;
};

/**
 * Deletes a folder from the graph.
 *
 *  - Reassigns the folder's child to the parent's child or exit.
 *  - Reassigns the folder's exit to the exit of the folder's last step.
 */
const deleteFolderStep = ({ stepId, parentStepId, dag }) => {
  const step = dag[stepId];
  const folderRootId = step.child;
  const folderEndId = getFolderExitStepId(folderRootId, dag);
  if (parentStepId) {
    const parentKey = getParentKey(stepId, parentStepId, dag);
    dag[parentStepId][parentKey] = folderRootId;
  }
  if (folderEndId && dag[folderEndId]) {
    dag[folderEndId].exit = step.exit;
  }

  delete dag[stepId];

  return dag;
};

const getParentKey = (stepId, parentStepId, dag) => {
  let parentKey = 'exit';
  if (dag[parentStepId]?.branches?.includes(stepId)) {
    parentKey = 'branches';
  } else if (dag[parentStepId]?.child === stepId) {
    parentKey = 'child';
  }
  return parentKey;
};

const getAllDeepChildrenAndBranches = (dag, stepId) => {
  const step = dag[stepId];
  const fullList = [];
  const directChildren = [step?.child, ...(step?.branches || [])].filter((n) => !!n);
  fullList.push(...directChildren);
  const iterate = (childStepId) => {
    const childStep = dag[childStepId];
    const childChildren = [childStep?.exit, childStep?.child, ...(childStep?.branches || [])].filter((n) => !!n && n !== childStepId);
    fullList.push(...childChildren);
    childChildren.forEach(iterate);
  };

  directChildren.forEach(iterate);
  return [...new Set(fullList)];
};

/**
 * Deletes a normal step from the graph:
 *
 *  - Recursively delete all children
 *  - Reassigns the step's exit to the parent's child or exit
 */
const deleteNormalStep = ({ stepId, parentStepId, dag, shallow = false }) => {
  const step = dag[stepId];

  if (shallow === false) {
    const deepChildren = getAllDeepChildrenAndBranches(dag, stepId);
    deepChildren.forEach((n) => {
      delete dag[n];
    });
  }

  // Reassigned the step's exit to the parent's child or exit.
  if (parentStepId) {
    const parentKey = getParentKey(stepId, parentStepId, dag);
    if (parentKey === 'branches') {
      const index = dag[parentStepId].branches.indexOf(stepId);
      dag[parentStepId].branches[index] = step.exit;
    } else {
      dag[parentStepId][parentKey] = step.exit;
    }
  }

  delete dag[stepId];

  return dag;
};

export const deleteStepFromGraph = (graph, stepId, shallow = false) => {
  const dag = { ...graph };
  if (!dag[stepId]) {
    return dag;
  }
  const step = { ...dag[stepId] };

  const isFolder = step?.type === 'folder';
  const parentStepId = getParentStepId(stepId, dag);

  if (isFolder && shallow !== true) {
    return deleteFolderStep({ stepId, parentStepId, dag });
  }

  return deleteNormalStep({ stepId, parentStepId, dag, shallow });
};
